import type { IEnableThemeHelperStatus } from '../constants';

import { useShopUpdateThemeExtensionMutation } from '~/api/graphql/mutations/shopUpdateThemeExtension.generated';
import { useEnableThemeAppExtension, IPlatformAppType } from '~/modules/apps';
import { ENABLE_THEME_HELPER_STATUS } from '../constants';
import { captureSentryError } from '~/exceptions/sentry';
import { getConfig } from '~/config/environment';
import { useState } from 'react';

const APP_CONFIG = getConfig('APP');
const SHOPIFY_THEME_EXTENSION_ID = APP_CONFIG?.SHOPIFY_THEME_EXTENSION_ID ?? '';
const SHOPIFY_THEME_EXTENSION_HANDLE = APP_CONFIG?.SHOPIFY_THEME_EXTENSION_HANDLE ?? '';
const GEMX_APP_HANDLE = APP_CONFIG?.GEMX_APP_HANDLE ?? '';

// Shopify theme app extension reference the enable mutation toggles.
const GEMX_EXTENSION_TYPE = `shopify://apps/${GEMX_APP_HANDLE}/blocks/${SHOPIFY_THEME_EXTENSION_HANDLE}/${SHOPIFY_THEME_EXTENSION_ID}`;

export const useOnboardingEnableThemeHelper = () => {
  const [status, setStatus] = useState<IEnableThemeHelperStatus>(ENABLE_THEME_HELPER_STATUS.Idle);

  // Reuse the app-wide theme-extension hook for status (`isShowBanner`) plus the editor fallback:
  // open editor → re-check on return via visibilitychange → loading — same as the dashboard banner.
  const { isShowBanner, isLoadingButton, onHandleEnableThemeAppExtension, setIsShowBanner } =
    useEnableThemeAppExtension();

  // isShowBanner: undefined = still checking, true = not enabled, false = enabled.
  const isAlreadyEnabled = isShowBanner === false;
  const isCheckingStatus = isShowBanner === undefined;

  const { mutate: updateThemeExtension } = useShopUpdateThemeExtensionMutation({
    onSuccess: () => {
      setIsShowBanner(false);
      setStatus(ENABLE_THEME_HELPER_STATUS.Success);
    },
    onError: (error) => {
      captureSentryError(error, {
        source: 'useOnboardingEnableThemeHelper.updateThemeExtension',
        tags: { shopify: 'Shopify' },
      });
      setStatus(ENABLE_THEME_HELPER_STATUS.Idle);
      // Fallback: send the merchant to the theme editor; the reused hook re-checks on return.
      onHandleEnableThemeAppExtension();
    },
  });

  const onEnable = () => {
    setStatus(ENABLE_THEME_HELPER_STATUS.Loading);
    updateThemeExtension({
      extensionType: GEMX_EXTENSION_TYPE,
      disable: false,
      // Without appType the backend falls back to the GemPages app, so it would toggle the
      // wrong extension/shop platform for this app.
      appType: IPlatformAppType.GEMXP,
    });
  };

  // The editor-fallback re-check drives its own loading flag; reflect it on the button.
  const buttonStatus = isLoadingButton ? ENABLE_THEME_HELPER_STATUS.Loading : status;

  return {
    status: buttonStatus,
    isAlreadyEnabled,
    isCheckingStatus,
    onEnable,
  };
};
